/**
 * Product Comparison Page
 * Side-by-side comparison of catalog products with spec diffing
 */

import { ArrowLeft, IndianRupee, Layers, Sliders, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Skeleton } from "@/components/ui/skeleton";
import {
	type CatalogProduct,
	compareProducts,
} from "@/features/catalog/services/catalog-api.service";
import { formatUom } from "@/features/catalog/utils/uom";
import { FloatPanel, StudioButton } from "@/features/studio/theme";
import { formatPricePaisa } from "@/lib/format";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const COMPARE_STORAGE_KEY = "catalog_compare_ids";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function syncCompareStorage(ids: string[]): void {
	try {
		localStorage.setItem(COMPARE_STORAGE_KEY, JSON.stringify(ids));
	} catch {
		// ignore storage errors
	}
}

// ---------------------------------------------------------------------------
// Loading skeleton
// ---------------------------------------------------------------------------

function ComparisonSkeleton(): React.ReactElement {
	return (
		<div className="space-y-6">
			<div className="flex gap-4 overflow-x-auto pb-2">
				{[1, 2, 3].map((i) => (
					<div key={i} className="min-w-60 flex-1 space-y-3">
						<Skeleton className="h-40 w-full rounded-lg" />
						<Skeleton className="h-5 w-3/4" />
						<Skeleton className="h-4 w-1/2" />
						<Skeleton className="h-4 w-1/3" />
					</div>
				))}
			</div>
			{[1, 2, 3, 4, 5].map((i) => (
				<Skeleton key={i} className="h-12 w-full" />
			))}
		</div>
	);
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ProductComparisonPage(): React.ReactElement {
	const navigate = useNavigate();
	const [searchParams, setSearchParams] = useSearchParams();
	const idsParam = searchParams.get("ids") ?? "";
	const ids = useMemo(() => {
		return idsParam
			.split(",")
			.map((s) => s.trim())
			.filter(Boolean);
	}, [idsParam]);

	const [products, setProducts] = useState<CatalogProduct[]>([]);
	const [isLoading, setIsLoading] = useState(true);

	// ---- Fetch comparison data ----
	const fetchProducts = useCallback(async (productIds: string[]) => {
		if (productIds.length < 2) {
			setIsLoading(false);
			return;
		}
		setIsLoading(true);
		try {
			const data = await compareProducts(productIds);
			setProducts(data);
		} finally {
			setIsLoading(false);
		}
	}, []);

	useEffect(() => {
		fetchProducts(ids);
	}, [fetchProducts, ids]);

	// ---- Remove a product from comparison ----
	const handleRemove = (productId: string) => {
		const next = ids.filter((id) => id !== productId);
		syncCompareStorage(next);
		if (next.length < 2) {
			navigate("/catalog");
			return;
		}
		setSearchParams({ ids: next.join(",") });
		setProducts((prev) => prev.filter((p) => p.id !== productId));
	};

	// ---- Collect all spec keys across products ----
	const allSpecKeys: string[] = [];
	const seenKeys = new Set<string>();
	for (const p of products) {
		if (p.specifications) {
			for (const key of Object.keys(p.specifications)) {
				if (!seenKeys.has(key)) {
					seenKeys.add(key);
					allSpecKeys.push(key);
				}
			}
		}
	}

	// ---- Not enough IDs ----
	if (ids.length < 2 && !isLoading) {
		return (
			<div className="min-h-screen bg-transparent p-4 sm:p-12">
				<div className="mx-auto max-w-2xl text-center py-20">
					<FloatPanel
						elevation={1}
						padding={48}
						style={{ background: "var(--color-dashboard-paper)" }}
					>
						<Layers className="mx-auto mb-6 h-12 w-12 text-[var(--color-dashboard-muted)]" />
						<h2
							style={{
								fontFamily: "var(--font-display)",
								fontWeight: 500,
								fontSize: 24,
								color: "var(--color-dashboard-text)",
							}}
						>
							Select at least 2 products to compare
						</h2>
						<p
							style={{
								fontFamily: "var(--font-body)",
								color: "var(--color-dashboard-muted)",
								marginTop: 12,
								fontSize: 14,
							}}
						>
							Browse the catalog and add products to your comparison tray to
							view details side-by-side.
						</p>
						<StudioButton
							variant="primary"
							style={{ marginTop: 24 }}
							onClick={() => navigate("/catalog")}
						>
							Browse Catalog
						</StudioButton>
					</FloatPanel>
				</div>
			</div>
		);
	}

	return (
		<div className="min-h-screen bg-transparent p-4 sm:p-12">
			<div className="mx-auto max-w-6xl space-y-8">
				{/* Header */}
				<FloatPanel
					elevation={1}
					padding={32}
					style={{ background: "var(--color-dashboard-paper)" }}
				>
					<div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
						<button
							type="button"
							onClick={() => navigate(-1)}
							style={{
								display: "inline-flex",
								alignItems: "center",
								gap: 6,
								fontSize: 12,
								fontWeight: 600,
								color: "var(--color-dashboard-muted)",
								cursor: "pointer",
								background: "none",
								border: "none",
								padding: 0,
								alignSelf: "flex-start",
							}}
						>
							<ArrowLeft className="h-4 w-4" />
							Back to Catalog
						</button>
						<div style={{ display: "flex", alignItems: "center", gap: 12 }}>
							<p
								className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold"
								style={{
									background:
										"color-mix(in srgb, var(--color-dashboard-accent) 10%, transparent)",
									color: "var(--color-dashboard-accent-dark)",
								}}
							>
								<Sliders className="h-3.5 w-3.5" />
								COMPARE
							</p>
						</div>
						<h1
							style={{
								fontFamily: "var(--font-display)",
								fontWeight: 500,
								fontSize: 36,
								color: "var(--color-dashboard-text)",
								letterSpacing: "-0.02em",
								margin: 0,
							}}
						>
							Compare{" "}
							<span
								style={{
									fontFamily: "var(--font-display)",
									fontStyle: "italic",
									color: "var(--color-dashboard-accent-display)",
								}}
							>
								Products
							</span>
						</h1>
					</div>
				</FloatPanel>

				{isLoading ? (
					<ComparisonSkeleton />
				) : products.length === 0 ? (
					<FloatPanel
						elevation={1}
						padding={48}
						style={{
							background: "var(--color-dashboard-paper)",
							textAlign: "center",
						}}
					>
						<p
							style={{
								fontFamily: "var(--font-body)",
								color: "var(--color-dashboard-muted)",
							}}
						>
							No products found for the given IDs.
						</p>
						<StudioButton
							variant="quiet"
							style={{ marginTop: 16 }}
							onClick={() => navigate("/catalog")}
						>
							Back to Catalog
						</StudioButton>
					</FloatPanel>
				) : (
					<FloatPanel
						elevation={1}
						padding={24}
						style={{
							background: "var(--color-dashboard-paper)",
							overflowX: "auto",
						}}
					>
						<table
							className="w-full border-collapse"
							style={{ minWidth: products.length * 240 }}
						>
							{/* ---------- Product header row ---------- */}
							<thead>
								<tr>
									<th
										style={{
											fontFamily: "var(--font-mono)",
											fontSize: 12,
											color: "var(--color-dashboard-muted)",
											textAlign: "left",
											padding: "16px 12px",
											borderBottom: "1px solid var(--color-dashboard-border)",
											width: 160,
										}}
									>
										Specifications
									</th>
									{products.map((product) => (
										<th
											key={product.id}
											style={{
												textAlign: "left",
												verticalAlign: "top",
												padding: "12px",
												borderBottom: "1px solid var(--color-dashboard-border)",
											}}
										>
											<div
												style={{
													position: "relative",
													display: "flex",
													flexDirection: "column",
													gap: 10,
												}}
											>
												{/* Remove button */}
												<button
													type="button"
													onClick={() => handleRemove(product.id)}
													style={{
														position: "absolute",
														right: 0,
														top: 0,
														display: "flex",
														alignItems: "center",
														justifyContent: "center",
														width: 24,
														height: 24,
														borderRadius: "50%",
														background: "var(--color-dashboard-divider)",
														border: "none",
														color: "var(--color-dashboard-muted)",
														cursor: "pointer",
													}}
												>
													<X className="h-4 w-4" />
												</button>

												{/* Image */}
												<div
													style={{
														height: 120,
														display: "flex",
														alignItems: "center",
														justifyContent: "center",
														borderRadius: 6,
														border: "1px solid var(--color-dashboard-border)",
														background: "var(--color-dashboard-paper)",
														overflow: "hidden",
													}}
												>
													{product.images?.[0] ? (
														<img
															src={product.images[0].url}
															/*
															 * The stored description when there is one. Comparison
															 * reads the detail shape, so it is one of the two
															 * surfaces that receives alt text at all -- and it shows
															 * several products side by side, where the product name
															 * is the least useful thing to announce.
															 */
															alt={product.images[0].alt || product.name}
															style={{
																height: "100%",
																width: "100%",
																objectFit: "contain",
															}}
														/>
													) : (
														<Layers className="h-8 w-8 text-[var(--color-dashboard-muted)]" />
													)}
												</div>

												{/* Details */}
												<div>
													<p
														style={{
															fontFamily: "var(--font-display)",
															fontWeight: 600,
															fontSize: 14,
															color: "var(--color-dashboard-text)",
															margin: 0,
															lineHeight: 1.2,
														}}
													>
														{product.name}
													</p>
													{product.brand?.name && (
														<p
															style={{
																fontFamily: "var(--font-body)",
																fontSize: 12,
																color: "var(--color-dashboard-muted)",
																marginTop: 2,
																margin: 0,
															}}
														>
															{product.brand.name}
														</p>
													)}
												</div>

												{/* Tier */}
												<div>
													<span
														style={{
															display: "inline-block",
															borderRadius: 9999,
															padding: "2px 8px",
															fontSize: 10,
															fontWeight: 600,
															textTransform: "uppercase",
															fontFamily: "var(--font-mono)",
															letterSpacing: "0.08em",
															background:
																product.tier === "luxury"
																	? "var(--color-dashboard-pill-awaiting-bg)"
																	: product.tier === "premium"
																		? "var(--color-dashboard-pill-info-bg)"
																		: "var(--color-dashboard-divider)",
															color:
																product.tier === "luxury"
																	? "var(--color-dashboard-warning)"
																	: product.tier === "premium"
																		? "var(--color-dashboard-info-text)"
																		: "var(--color-dashboard-muted-dark)",
														}}
													>
														{product.tier}
													</span>
												</div>

												{/* Price */}
												<div
													style={{
														display: "flex",
														alignItems: "baseline",
														gap: 4,
														fontSize: 14,
														fontFamily: "var(--font-body)",
														color: "var(--color-dashboard-text)",
													}}
												>
													<IndianRupee className="h-3 w-3 text-[var(--color-dashboard-muted)]" />
													{/*
													 * This screen exists to put products side by side, so
													 * it is the one place where an unnormalised figure
													 * does the most damage: `mrp` is GST-inclusive from
													 * some brands and exclusive from others, and reading
													 * it here rendered an ~18% difference in tax
													 * treatment as an ~18% difference in price. Both
													 * numbers looked equally authoritative.
													 *
													 * `price.amount` is normalised to GST-inclusive, so
													 * the comparison is between like and like.
													 */}
													{product.price ? (
														<span style={{ fontWeight: 600 }}>
															{formatPricePaisa(product.price.amount)}
														</span>
													) : (
														<span
															style={{ color: "var(--color-dashboard-muted)" }}
														>
															Price on request
														</span>
													)}
													{(product.price?.uom ?? product.uom) && (
														<span
															style={{
																color: "var(--color-dashboard-muted)",
																fontSize: 11,
															}}
														>
															/{formatUom(product.price?.uom ?? product.uom)}
														</span>
													)}
												</div>
												{/*
												 * Normalising makes the numbers comparable; it does not
												 * make them the same KIND of number. A trade price and
												 * an MRP can be equal and still mean different things
												 * to someone deciding what to buy, so each column says
												 * which it is, and flags one nobody has re-checked.
												 */}
												{product.price && (
													<div
														style={{
															fontSize: 11,
															color: product.price.isStale
																? "var(--color-dashboard-pill-awaiting)"
																: "var(--color-dashboard-muted)",
															fontFamily: "var(--font-body)",
														}}
													>
														{product.price.basisLabel}
														{product.price.isStale && " · needs re-checking"}
													</div>
												)}
											</div>
										</th>
									))}
								</tr>
							</thead>

							{/* ---------- Specification rows ---------- */}
							<tbody>
								{allSpecKeys.map((specKey) => {
									const values = products.map(
										(p) => p.specifications?.[specKey]?.toString() ?? "—",
									);
									const uniqueValues = new Set(values);
									const hasDifference = uniqueValues.size > 1;

									return (
										<tr
											key={specKey}
											style={{
												borderBottom:
													"1px solid var(--color-dashboard-border-soft)",
											}}
										>
											<td
												style={{
													padding: "14px 12px",
													fontSize: 13,
													fontWeight: 500,
													color: "var(--color-dashboard-muted-dark)",
													fontFamily: "var(--font-body)",
												}}
											>
												{specKey}
											</td>
											{values.map((val, i) => (
												<td
													key={products[i].id}
													style={{
														padding: "14px 12px",
														fontSize: 13,
														color: "var(--color-dashboard-text)",
														fontFamily: "var(--font-body)",
														background: hasDifference
															? "color-mix(in srgb, var(--color-dashboard-accent) 3%, transparent)"
															: "transparent",
													}}
												>
													{val}
												</td>
											))}
										</tr>
									);
								})}

								{allSpecKeys.length === 0 && (
									<tr>
										<td
											colSpan={products.length + 1}
											style={{
												padding: "36px",
												textAlign: "center",
												fontSize: 13,
												color: "var(--color-dashboard-muted)",
												fontFamily: "var(--font-body)",
											}}
										>
											No specifications available for these products.
										</td>
									</tr>
								)}
							</tbody>
						</table>
					</FloatPanel>
				)}
			</div>
		</div>
	);
}
